// Which features a club can actually use.
//
// FEATURE_CATALOGUE is the list of features the FAN APP knows about. It is not
// the list a given club can use, and the screen used to show all of it to
// everyone.
//
// The profile screen is signed-in only — every door to it refuses an anonymous
// session, the server will not issue an offer code to one, and it has no email
// or name to show. So a club whose app has no login flow at all (Settings →
// Hide Login Flow) cannot use the profile, and cannot use the sections that
// live inside it either. Charlotte Checkers is exactly that club today: a
// switch for MY OFFERS there offers something no fan of theirs can ever open.
//
// Two independent facts, which this file used to conflate into one chain.
//
// `containedIn` is the feature this one cannot work without — where it is
// DRAWN, for every section but the Choose Experience screen, which is a screen
// of its own that only works alongside the Change Experience menu. The same
// containment the fan app walks in team_app_react_native/src/utils/
// featureGating.js, so the two agree about what contains what. Ours to change.
//
// `needs` is what the CLUB must have, declared on the feature that actually
// needs it and never inherited. The catalogue was born describing containment
// only, and capability requirements were later bolted onto that same chain —
// so a link offer, which needs no account, no server call and no Ticketmaster,
// was refused an account it never wanted because of the screen it happens to
// be drawn on.
//
// The exception is state that already exists. A feature that is switched on, or
// that carries a per-person override, keeps its row even when it is
// unavailable: hiding a live switch leaves it changing the app with nothing on
// screen that can turn it back off. Such a row is marked, and drops out of the
// list once it is off and its overrides are cleared.

/**
 * Can any fan of this club sign in?
 *
 * @param rootSettings the ROOT `settings` node, and only that.
 *
 * It used to ask whether ANY experience allowed login, reasoning that a fan
 * reads the settings of the experience they are in. The app does not work that
 * way: App.js subscribes the root settings first — the experience prefix is ''
 * until one resolves — and latches `skipLoginFlow` from them, and nothing ever
 * resets it. So root decides, for every fan, whatever an experience says.
 *
 * Worse, the old question could not answer no. The DEFAULT experience keeps its
 * settings at the root and so has no `settings` child of its own, contributing
 * an empty object to the `.some()` — which passes. Any club that had ever run
 * the Experiences setup was therefore scored "login active" regardless, and
 * Checkers, whose fans cannot sign in at all, would have been offered the
 * account-only switches the moment they created their first experience.
 *
 * Unknown (a missing node, a read that failed) still reads as YES: a switch
 * missing because a read was slow is worse than one that turns out to do
 * nothing.
 */
export function loginIsActive(rootSettings) {
    return !rootSettings || rootSettings.hideLoginFlow !== true;
}

// The capabilities a feature can require. A club either has one or it does not;
// nothing in this admin can grant them.
export const CAPABILITIES = {
    // Fans can sign in at all — settings.hideLoginFlow on the ROOT node.
    FAN_ACCOUNTS: 'fanAccounts',
    // The SERVER can confirm who a fan is. Not a build-time fact and not
    // visible from the browser: it is TM_API_KEY in the functions environment,
    // reported back by /serverCapabilities.
    VERIFIED_IDENTITY: 'verifiedIdentity'
};

/**
 * Can the SERVER confirm who a fan is?
 *
 * This used to read `REACT_APP_HAS_TICKETMASTER`, which was wrong twice over.
 * That flag means "this club sells tickets through Ticketmaster in-app" — it
 * gates the ticket entries an operator can add to the side menu, home buttons
 * and bottom tabs — and it says nothing about whether a verification can
 * complete. And the justification behind it was false: every fan binary links
 * the Ignite SDK on both platforms, so there is no build-time fact separating a
 * Ticketmaster club from a non-Ticketmaster one.
 *
 * What actually decides it is TM_API_KEY in the FUNCTIONS environment, which
 * the admin browser cannot see. So the server reports it — see
 * /serverCapabilities — and this reads what came back.
 *
 * Unknown reads as YES, the same bargain loginIsActive makes: a switch missing
 * because a request was slow is worse than one that turns out to do nothing.
 */
export function identityIsVerifiable(serverCapabilities) {
    if (!serverCapabilities) return true;
    return serverCapabilities.ticketmasterConfigured !== false;
}

/**
 * Can this club use this feature?
 *
 * Two separate questions, which used to be one. A feature declares what IT
 * needs in `needs`, and where it is DRAWN in `containedIn`. The walk still
 * climbs the containment chain — but not because requirements are inherited.
 * It climbs because a section drawn inside a container this club cannot have is
 * a section no fan can ever reach, whatever the section itself needs.
 *
 * The distinction is the point. `offers: { needs: [] }` is the true statement —
 * a link offer needs no account, no server call and no Ticketmaster — and it is
 * withheld from a no-login club today only because the profile screen is the
 * one place it is drawn. Move it somewhere reachable and it frees itself, with
 * no capability declaration to remember to change.
 *
 * An `undefined` capability means not known yet, which never hides anything.
 */
export function isAvailable(feature, capabilities = {}, catalogue = []) {
    let node = feature;
    let steps = 0;
    while (node) {
        const unmet = (node.needs || []).some(capability => capabilities[capability] === false);
        if (unmet) return false;
        if (++steps > 10) return true;
        node = node.containedIn
            ? (catalogue || []).find(entry => entry.id === node.containedIn)
            : null;
    }
    return true;
}

// Is this feature doing anything right now — on for everyone, or set either way
// for one person? Both are reasons to keep a switch on screen.
export function hasLiveState(featureId, { flags = {}, access = {} } = {}) {
    const flag = flags && flags[featureId];
    if (flag && flag.enabled === true) return true;
    return Object.values(access || {}).some(entry =>
        entry && (entry[featureId] === true || entry[featureId] === false));
}

/**
 * The catalogue rows to render.
 *
 * @param catalogue    FEATURE_CATALOGUE from SetUpFeatureFlags
 * @param capabilities what this club has — { login } today
 * @param state        { flags, access } as read from RTDB
 * @returns the features to show, each carrying `unavailable` — true for a row
 *          that is only still here because it has state to turn off.
 */
export function visibleFeatures(catalogue = [], capabilities = {}, state = {}) {
    return catalogue
        .map(feature => ({
            ...feature,
            unavailable: !isAvailable(feature, capabilities, catalogue)
        }))
        .filter(feature => !feature.unavailable || hasLiveState(feature.id, state));
}
